import React, { useState } from 'react';
import { Trans } from '@lingui/macro';
import moment from 'moment';
import { numberWithSpaces } from '../../../helpers/index';

/** A top-day entry — supports both old {date,count} and new API {day_date,total} shapes */
interface ITopDay {
    date?: string;
    day_date?: string;
    count?: number;
    total?: number;
}

/** Slovak day-of-week names */
const WEEKDAY_SK = ['Nedeľa', 'Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota'];

interface IProps {
    title: string;
    subtitle?: string;
    days: ITopDay[];
}

type SortCol = 'date' | 'day' | 'value';
type SortDir = 'asc' | 'desc';

/**
 * Table showing the top N days by footfall count.
 * Displays rank, date, day-of-week, and count with medal styling for top 3.
 */
const ReportTopDaysTable = ({ title, subtitle, days }: IProps) => {
    const [sortCol, setSortCol] = useState<SortCol>('value');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    if (!days || days.length === 0) return null;

    const handleSort = (col: SortCol) => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCol(col);
            setSortDir(col === 'value' ? 'desc' : 'asc');
        }
    };

    const enriched = days.map(day => {
        const dateStr = day.day_date || day.date || '';
        const value = day.total ?? day.count ?? 0;
        const jsDay = new Date(dateStr + 'T00:00:00').getDay();
        return { day, dateStr, value, jsDay, dayName: WEEKDAY_SK[jsDay], isWeekend: jsDay === 0 || jsDay === 6 };
    });

    const sorted = [...enriched].sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;
        if (sortCol === 'date') { aVal = a.dateStr; bVal = b.dateStr; }
        else if (sortCol === 'day') { aVal = a.jsDay; bVal = b.jsDay; }
        else { aVal = a.value; bVal = b.value; }
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const SortIcon = ({ col }: { col: SortCol }) => {
        if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
        return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    const getMedalStyle = (rank: number): React.CSSProperties => {
        switch (rank) {
            case 1: return { background: 'linear-gradient(135deg, #FFD700, #FFC107)', color: '#7B5800', fontWeight: 700, borderRadius: '50%', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' };
            case 2: return { background: 'linear-gradient(135deg, #C0C0C0, #A8A8A8)', color: '#444', fontWeight: 700, borderRadius: '50%', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' };
            case 3: return { background: 'linear-gradient(135deg, #CD7F32, #B87333)', color: '#fff', fontWeight: 700, borderRadius: '50%', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' };
            default: return { color: '#666', fontWeight: 600, fontSize: '13px', display: 'inline-block', width: 28, textAlign: 'center' };
        }
    };

    return (
        <div className="mgt-40">
            <h3 className="title_h4">{title}</h3>
            {subtitle && <p className="text_s12 color_grey mgt-5">{subtitle}</p>}

            <div className="report-table-wrap mgt-20">
              <div className="table_scroll">
                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: 60, textAlign: 'center' }}>#</th>
                            <th
                                onClick={() => handleSort('date')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                <Trans>Dátum</Trans><SortIcon col="date" />
                            </th>
                            <th
                                onClick={() => handleSort('day')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                <Trans>Deň</Trans><SortIcon col="day" />
                            </th>
                            <th
                                onClick={() => handleSort('value')}
                                style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                            >
                                <Trans>Návštevnosť</Trans><SortIcon col="value" />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map(({ dateStr, value, dayName, isWeekend }, i) => {
                            const m = moment(dateStr);
                            return (
                                <tr key={dateStr} style={isWeekend ? { backgroundColor: '#FFF8F0' } : undefined}>
                                    <td style={{ textAlign: 'center', color: '#666', fontWeight: 600, fontSize: '13px' }}>
                                        {i + 1}
                                    </td>
                                    <td>
                                        <strong>{m.format('DD.MM.YYYY')}</strong>
                                    </td>
                                    <td>
                                        <span style={isWeekend ? { color: '#A81A40', fontWeight: 600 } : undefined}>
                                            {dayName}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <strong style={{ fontSize: '14px' }}>
                                            {numberWithSpaces(value)}
                                        </strong>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
              </div>
            </div>
        </div>
    );
};

export default ReportTopDaysTable;
